import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import SyncIcon from '@mui/icons-material/Sync'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useTranslation } from 'react-i18next'

import { adminApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import { ErrorState, EmptyState } from '../../components/common/States'

/**
 * Channel calendar sync.
 *
 * Import feeds are pulled from Booking.com and Airbnb on a schedule; the export feed is the
 * URL the owner pastes into those channels so they can see nights sold on this site. Both
 * directions have to exist or the owner is still reconciling two calendars by hand, which is
 * how a cottage gets sold twice for the same week in July.
 */
export function AdminFeedsPage() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  const feeds = useQuery({ queryKey: ['admin', 'feeds'], queryFn: adminApi.icalFeeds })
  const units = useQuery({ queryKey: ['admin', 'units', locale], queryFn: () => adminApi.units(locale) })

  const sync = useMutation({
    mutationFn: (id: string) => adminApi.syncIcalFeed(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'feeds'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'calendar'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteIcalFeed(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'feeds'] }),
  })

  const copy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(id)
      window.setTimeout(() => setCopied(null), 2000)
    } catch {
      // Clipboard access can be refused; the URL is visible on screen either way.
    }
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h2">{t('admin.feeds')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
          {t('admin.addActivity').replace(/.*/, '+ iCal')}
        </Button>
      </Stack>

      {feeds.isError && <ErrorState error={feeds.error} onRetry={() => void feeds.refetch()} />}

      {/* ---------------------------------------------------------------- export URLs */}
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        {t('admin.exportUrl')}
      </Typography>
      <Stack spacing={1} sx={{ mb: 5 }}>
        {(units.data ?? []).map((unit) => {
          const url = `${window.location.origin}/ical/units/<token>.ics`
          return (
            <Paper
              key={unit.id}
              elevation={0}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Typography variant="body2" sx={{ minWidth: 170, fontWeight: 600 }}>
                {unit.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  flex: 1,
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {url}
              </Typography>
              <Tooltip title={copied === unit.id ? t('admin.copied') : ''}>
                <IconButton size="small" onClick={() => void copy(url, unit.id)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          )
        })}
        <Typography variant="caption" color="text.secondary">
          The token for each unit is issued by the API. Fetch it from the unit editor, or rotate it
          there to revoke every subscription at once.
        </Typography>
      </Stack>

      {/* ---------------------------------------------------------------- import feeds */}
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        Import
      </Typography>

      {feeds.data?.length === 0 && (
        <EmptyState icon="🔁" title="No channel calendars yet" body="Add the .ics URL Booking.com or Airbnb gives you." />
      )}

      <Stack spacing={1.5}>
        {(feeds.data ?? []).map((feed) => (
          <Paper
            key={feed.id}
            elevation={0}
            sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 3 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {feed.name}
                  </Typography>
                  <Chip size="small" label={feed.direction} variant="outlined" />
                  <Chip
                    size="small"
                    label={feed.lastStatus}
                    color={
                      feed.lastStatus === 'OK' ? 'success' : feed.lastStatus === 'FAILED' ? 'error' : 'default'
                    }
                  />
                  {!feed.active && <Chip size="small" label="Paused" />}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {feed.unitName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {feed.url}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('admin.lastSynced')}:{' '}
                  {feed.lastSyncedAt ? new Date(feed.lastSyncedAt).toLocaleString() : t('admin.neverSynced')}
                  {feed.lastMessage ? ` · ${feed.lastMessage}` : ''}
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.5}>
                <Button
                  size="small"
                  startIcon={<SyncIcon />}
                  disabled={sync.isPending || feed.direction !== 'IMPORT'}
                  onClick={() => sync.mutate(feed.id)}
                >
                  {t('admin.syncNow')}
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    if (window.confirm(t('admin.deleteConfirm'))) remove.mutate(feed.id)
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {sync.isSuccess && (
        <Alert severity="success" sx={{ mt: 2, borderRadius: 3 }}>
          {sync.data.lastMessage}
        </Alert>
      )}
      {sync.isError && <ErrorState error={sync.error} />}

      {adding && (
        <AddFeedDialog
          units={(units.data ?? []).map((unit) => ({ id: unit.id, name: unit.name }))}
          onClose={() => setAdding(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['admin', 'feeds'] })
            setAdding(false)
          }}
        />
      )}
    </Box>
  )
}

function AddFeedDialog({
  units,
  onClose,
  onSaved,
}: {
  units: { id: string; name: string }[]
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [unitId, setUnitId] = useState(units[0]?.id ?? '')
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')

  const create = useMutation({
    mutationFn: () =>
      adminApi.createIcalFeed({ unitId, name, url, direction: 'IMPORT', active: true }),
    onSuccess: onSaved,
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a channel calendar</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label={t('admin.units')}
            value={unitId}
            onChange={(event) => setUnitId(event.target.value)}
          >
            {units.map((unit) => (
              <MenuItem key={unit.id} value={unit.id}>
                {unit.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            label="Name"
            placeholder="Booking.com"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <TextField
            required
            label="iCal URL"
            placeholder="https://admin.booking.com/hotel/hoteladmin/ical.html?t=…"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
          {create.isError && <ErrorState error={create.error} />}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={() => create.mutate()} disabled={create.isPending}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
